import { useState } from 'react'
import { Button, Modal } from '@/components/ui'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs.tsx'
import { updateKnowledgeSourcePriority, type AnalysisResult } from '@/api/knowledgeBase.ts'

// ─── Priority badge ───────────────────────────────────────────────────────────

const PRIORITY_STYLES: Record<number, { label: string; className: string }> = {
  1: { label: 'P1 – Kritisch', className: 'bg-red-100 text-red-700' },
  2: { label: 'P2 – Hoch', className: 'bg-orange-100 text-orange-700' },
  3: { label: 'P3 – Mittel', className: 'bg-yellow-100 text-yellow-800' },
  4: { label: 'P4 – Niedrig', className: 'bg-green-100 text-green-700' },
  5: { label: 'P5 – Archiv', className: 'bg-gray-100 text-gray-500' },
}

function PriorityPill({ priority }: { priority: number }) {
  const style = PRIORITY_STYLES[priority] ?? { label: `P${priority}`, className: 'bg-gray-100 text-gray-600' }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${style.className}`}>
      {style.label}
    </span>
  )
}

// ─── Tab count badge ──────────────────────────────────────────────────────────

function CountBadge({ count }: { count: number }) {
  if (count === 0) return null
  return (
    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-semibold bg-violet-100 text-violet-700">
      {count}
    </span>
  )
}

// ─── Merge candidates tab ─────────────────────────────────────────────────────

function MergeCandidatesTab({ candidates }: { candidates: AnalysisResult['mergeCandidates'] }) {
  if (candidates.length === 0) {
    return (
      <p className="text-sm text-gray-500 py-4 text-center">
        Keine ähnlichen Einträge gefunden (Schwellwert: 88&nbsp;% Cosine-Similarity).
      </p>
    )
  }
  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500 mb-3">
        Diese Eintragspaare sind sich sehr ähnlich und könnten zusammengeführt werden.
        Das Zusammenführen muss manuell erfolgen.
      </p>
      {candidates.map((c) => (
        <div key={`${c.sourceIdA}-${c.sourceIdB}`} className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-medium text-gray-800 truncate max-w-[200px]">{c.titleA}</span>
              <span className="text-gray-400">↔</span>
              <span className="font-medium text-gray-800 truncate max-w-[200px]">{c.titleB}</span>
            </div>
          </div>
          <span className="shrink-0 text-xs font-mono bg-violet-50 text-violet-700 px-2 py-0.5 rounded">
            {(c.similarity * 100).toFixed(1)}&nbsp;%
          </span>
        </div>
      ))}
    </div>
  )
}

// ─── Contradictions tab ───────────────────────────────────────────────────────

function ContradictionsTab({ contradictions }: { contradictions: AnalysisResult['contradictions'] }) {
  if (contradictions.length === 0) {
    return (
      <p className="text-sm text-gray-500 py-4 text-center">
        Keine inhaltlichen Widersprüche erkannt.
      </p>
    )
  }
  return (
    <div className="space-y-2">
      {contradictions.map((c, i) => (
        <div key={i} className="p-3 rounded-lg border border-amber-100 bg-amber-50 space-y-1">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-medium text-gray-800">{c.sourceIdA.slice(0, 8)}</span>
            <span className="text-gray-400">↔</span>
            <span className="font-medium text-gray-800">{c.sourceIdB.slice(0, 8)}</span>
          </div>
          <p className="text-xs text-amber-800">{c.reason}</p>
        </div>
      ))}
    </div>
  )
}

// ─── Priority suggestions tab ─────────────────────────────────────────────────

function PrioritySuggestionsTab({
  suggestions,
  onApplied,
}: {
  suggestions: AnalysisResult['prioritySuggestions']
  onApplied: () => void
}) {
  const [applying, setApplying] = useState<Set<string>>(new Set())
  const [applied, setApplied] = useState<Set<string>>(new Set())
  const [applyAllLoading, setApplyAllLoading] = useState(false)
  const [error, setError] = useState('')

  const applyOne = async (sourceId: string, priority: number) => {
    setApplying((prev) => new Set(prev).add(sourceId))
    setError('')
    try {
      await updateKnowledgeSourcePriority(sourceId, priority)
      setApplied((prev) => new Set(prev).add(sourceId))
      onApplied()
    } catch {
      setError('Fehler beim Speichern der Priorität.')
    } finally {
      setApplying((prev) => { const s = new Set(prev); s.delete(sourceId); return s })
    }
  }

  const applyAll = async () => {
    const pending = suggestions.filter((s) => !applied.has(s.sourceId))
    if (pending.length === 0) return
    setApplyAllLoading(true)
    setError('')
    try {
      await Promise.all(pending.map((s) => updateKnowledgeSourcePriority(s.sourceId, s.suggestedPriority)))
      setApplied(new Set(suggestions.map((s) => s.sourceId)))
      onApplied()
    } catch {
      setError('Fehler beim Speichern einzelner Prioritäten.')
    } finally {
      setApplyAllLoading(false)
    }
  }

  if (suggestions.length === 0) {
    return (
      <p className="text-sm text-gray-500 py-4 text-center">
        Keine Prioritätsvorschläge verfügbar.
      </p>
    )
  }

  const pendingCount = suggestions.filter((s) => !applied.has(s.sourceId)).length

  return (
    <div className="space-y-3">
      {pendingCount > 0 && (
        <div className="flex justify-end">
          <Button variant="secondary" size="sm" onClick={applyAll} disabled={applyAllLoading}>
            {applyAllLoading ? 'Wird angewendet…' : `Alle anwenden (${pendingCount})`}
          </Button>
        </div>
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="space-y-2">
        {suggestions.map((s) => {
          const isApplied = applied.has(s.sourceId)
          const isApplying = applying.has(s.sourceId)
          return (
            <div key={s.sourceId} className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50">
              <div className="flex-1 min-w-0 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono text-gray-400">{s.sourceId.slice(0, 8)}</span>
                  <PriorityPill priority={s.suggestedPriority} />
                </div>
                <p className="text-xs text-gray-600">{s.reason}</p>
              </div>
              {isApplied ? (
                <span className="shrink-0 text-xs text-green-600 font-medium py-1">Angewendet</span>
              ) : (
                <Button
                  variant="secondary"
                  size="xs"
                  onClick={() => applyOne(s.sourceId, s.suggestedPriority)}
                  disabled={isApplying}
                >
                  {isApplying ? '…' : 'Anwenden'}
                </Button>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Main modal ───────────────────────────────────────────────────────────────

interface OptimizationResultModalProps {
  open: boolean
  result: AnalysisResult | null
  onClose: () => void
  onPrioritiesApplied: () => void
}

export function OptimizationResultModal({
  open,
  result,
  onClose,
  onPrioritiesApplied,
}: OptimizationResultModalProps) {
  if (!result) return null

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose() }} title="Analyse & Optimierungsvorschläge" size="lg">
      {result.truncated && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded px-3 py-2 mb-4">
          Hinweis: Es wurden nur die ersten 200 Einträge analysiert.
        </p>
      )}
      <p className="text-xs text-gray-500 mb-4">
        {result.sourcesAnalyzed} Einträge analysiert
      </p>

      <Tabs defaultValue="merge">
        <TabsList>
          <TabsTrigger value="merge">
            Zusammenführen <CountBadge count={result.mergeCandidates.length} />
          </TabsTrigger>
          <TabsTrigger value="contradictions">
            Widersprüche <CountBadge count={result.contradictions.length} />
          </TabsTrigger>
          <TabsTrigger value="priorities">
            Prioritäten <CountBadge count={result.prioritySuggestions.length} />
          </TabsTrigger>
        </TabsList>

        <TabsContent value="merge">
          <MergeCandidatesTab candidates={result.mergeCandidates} />
        </TabsContent>
        <TabsContent value="contradictions">
          <ContradictionsTab contradictions={result.contradictions} />
        </TabsContent>
        <TabsContent value="priorities">
          <PrioritySuggestionsTab
            suggestions={result.prioritySuggestions}
            onApplied={onPrioritiesApplied}
          />
        </TabsContent>
      </Tabs>

      <div className="flex justify-end mt-6">
        <Button variant="ghost" onClick={onClose}>Schließen</Button>
      </div>
    </Modal>
  )
}
